import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Truck, Package, Zap, MapPin, Loader2, Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Checkbox } from '@/components/atoms/checkbox';
import { Skeleton } from '@/components/atoms/skeleton';

import { useShippingRates } from '../hooks/use-shipping-rates';
import {
  useShopShippingRates,
  useAssociateShippingRates,
  useDisassociateShippingRates,
} from '../hooks/use-shop-shipping-rates';
import { formatPrice, getAmountFromPriceObject } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import type { ShippingRate, DeliveryType } from '../types/shipping.types';

interface ShippingRatesSelectorProps {
  shopRefId: string;
}

const DELIVERY_TYPE_ICONS: Record<DeliveryType, React.ReactNode> = {
  standard: <Truck className="size-4" />,
  express: <Zap className="size-4" />,
  same_day: <Zap className="size-4 text-orange-500" />,
  pickup: <MapPin className="size-4" />,
};

export function ShippingRatesSelector({ shopRefId }: ShippingRatesSelectorProps) {
  const { t } = useTranslation();
  const { currency } = useConfigStore();
  const [selectedRates, setSelectedRates] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch all seller's shipping rates
  const { data: allRates = [], isLoading: isLoadingRates } = useShippingRates();

  // Fetch shop's current shipping rates
  const { data: shopRates = [], isLoading: isLoadingShopRates } = useShopShippingRates(shopRefId);

  // Mutations
  const associateMutation = useAssociateShippingRates();
  const disassociateMutation = useDisassociateShippingRates();

  const isLoading = isLoadingRates || isLoadingShopRates;
  const isSaving = associateMutation.isPending || disassociateMutation.isPending;

  // Initialize selected rates from shop's current rates
  useEffect(() => {
    if (shopRates.length > 0 && !hasChanges) {
      const shopRateIds = new Set(shopRates.map((rate) => rate.refId));
      setSelectedRates(shopRateIds);
    }
  }, [shopRates]);

  // Get current shop rate IDs for comparison
  const currentShopRateIds = useMemo(
    () => new Set(shopRates.map((rate) => rate.refId)),
    [shopRates]
  );

  const handleToggleRate = (refId: string) => {
    setSelectedRates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(refId)) {
        newSet.delete(refId);
      } else {
        newSet.add(refId);
      }
      return newSet;
    });
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    const selectedArray = Array.from(selectedRates);
    const currentArray = Array.from(currentShopRateIds);

    // Find rates to add (in selected but not in current)
    const ratesToAdd = selectedArray.filter((id) => !currentShopRateIds.has(id));

    // Find rates to remove (in current but not in selected)
    const ratesToRemove = currentArray.filter((id) => !selectedRates.has(id));

    try {
      // Disassociate rates that were removed
      if (ratesToRemove.length > 0) {
        await disassociateMutation.mutateAsync({
          shopRefId,
          rateRefIds: ratesToRemove,
        });
      }

      // Associate new rates
      if (ratesToAdd.length > 0) {
        await associateMutation.mutateAsync({
          shopRefId,
          rateRefIds: ratesToAdd,
        });
      }

      toast.success(t('shipping.ratesUpdated'));
      setHasChanges(false);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const formatRatePrice = (rate: ShippingRate) => {
    const fallbackPrice = parseFloat(rate.price) || 0;
    if (fallbackPrice === 0) return t('shipping.free');
    return formatPrice(getAmountFromPriceObject(rate.prices, currency, fallbackPrice), currency);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (allRates.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="size-5" />
            {t('shipping.deliveryMethods')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <Package className="size-12 mx-auto mb-3 opacity-50" />
            <p>{t('shipping.noRatesCreated')}</p>
            <Button variant="link" asChild className="mt-2">
              <a href="/shipping/rates/new">{t('shipping.createFirstRate')}</a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="size-5" />
              {t('shipping.deliveryMethods')}
            </CardTitle>
            <CardDescription>{t('shipping.selectDeliveryMethodsDescription')}</CardDescription>
          </div>
          <Button variant="outline" size="sm" asChild className="w-full sm:w-auto">
            <Link to="/shipping/rates/new">
              <Plus className="size-4 mr-1" />
              {t('shipping.addRate')}
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {allRates.map((rate) => {
          const isSelected = selectedRates.has(rate.refId);
          const isCurrentlyAssociated = currentShopRateIds.has(rate.refId);

          return (
            <label
              key={rate.refId}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'hover:bg-muted/50'
              } ${!rate.isActive ? 'opacity-50' : ''}`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => handleToggleRate(rate.refId)}
                disabled={!rate.isActive}
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {DELIVERY_TYPE_ICONS[rate.deliveryType]}
                  <span className="font-medium">{rate.name}</span>
                  {!rate.isActive && (
                    <Badge variant="secondary" className="text-xs">
                      {t('common.inactive')}
                    </Badge>
                  )}
                  {isCurrentlyAssociated && !hasChanges && (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Check className="size-3" />
                      {t('shipping.associated')}
                    </Badge>
                  )}
                </div>
                {rate.description && (
                  <p className="text-sm text-muted-foreground truncate">{rate.description}</p>
                )}
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span>{formatRatePrice(rate)}</span>
                  {rate.estimatedDeliveryDays && (
                    <span>
                      {rate.estimatedDeliveryDays}-{rate.maxDeliveryDays || rate.estimatedDeliveryDays} {t('shipping.days')}
                    </span>
                  )}
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                asChild
                onClick={(e) => e.stopPropagation()}
              >
                <Link to={`/shipping/rates/${rate.refId}/edit`}>
                  <Pencil className="size-4" />
                </Link>
              </Button>
            </label>
          );
        })}

        {hasChanges && (
          <div className="pt-3 border-t">
            <Button
              onClick={handleSaveChanges}
              disabled={isSaving}
              className="w-full"
            >
              {isSaving && <Loader2 className="size-4 mr-2 animate-spin" />}
              {t('shipping.saveDeliveryMethods')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
